import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import t from '../translations';
import { MapPin, Clock, Calendar, X } from 'lucide-react';

export default function BirthDetailsPage() {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const { lang } = useLang();
  const tr = t[lang];

  const [birthDate,    setBirthDate]    = useState('');
  const [birthTime,    setBirthTime]    = useState('');
  const [unknownTime,  setUnknownTime]  = useState(false);
  const [cityQuery,    setCityQuery]    = useState('');
  const [cities,       setCities]       = useState([]);
  const [selected,     setSelected]     = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [searching,    setSearching]    = useState(false);
  const [error,        setError]        = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Nominatim city search — India priority, global fallback
  useEffect(() => {
    if (cityQuery.length < 2) { setCities([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        // First try India-only for faster, more relevant results
        const [indRes, globalRes] = await Promise.allSettled([
          fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityQuery)}&format=json&limit=5&addressdetails=1&countrycodes=in`, { headers: { 'Accept-Language': 'en' } }).then(r => r.json()),
          fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityQuery)}&format=json&limit=5&addressdetails=1`, { headers: { 'Accept-Language': 'en' } }).then(r => r.json()),
        ]);
        const indData    = indRes.status === 'fulfilled' ? indRes.value : [];
        const globalData = globalRes.status === 'fulfilled' ? globalRes.value : [];
        // Merge: India first, then global (deduped by name)
        const seen = new Set();
        const mapped = [...indData, ...globalData]
          .filter(d => d.lat && d.lon)
          .map(d => {
            const a    = d.address || {};
            const city = a.city || a.town || a.village || a.county || a.municipality || '';
            const state = a.state || a.region || '';
            const country = a.country || '';
            const name = [city, state, country].filter(Boolean).join(', ');
            return { name, lat: parseFloat(d.lat), lng: parseFloat(d.lon), country };
          })
          .filter(d => {
            if (!d.name || seen.has(d.name)) return false;
            seen.add(d.name); return true;
          })
          .slice(0, 7);
        setCities(mapped);
        if (mapped.length > 0) setShowDropdown(true);
      } catch { /* silent */ }
      setSearching(false);
    }, 450);
    return () => clearTimeout(timer);
  }, [cityQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pickCity = (city) => {
    setSelected(city);
    setCityQuery(city.name);
    setShowDropdown(false);
    setCities([]);
  };

  const clearTime = () => setBirthTime('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) { setError('Please select a city from the suggestions.'); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/api/user/birth-details', {
        birthDate,
        birthTime: unknownTime ? null : (birthTime || null),
        birthCity: selected.name,
        birthLat:  selected.lat,
        birthLng:  selected.lng,
      });
      navigate('/chart-loading');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div
      data-testid="birth-details-page"
      style={{
        minHeight: '100vh', padding: '56px 24px 40px',
        background: '#0A0F1E', boxSizing: 'border-box', width: '100%',
      }}
    >
      {/* Header */}
      <div className="fade-in-up" style={{ textAlign: 'center', marginBottom: 36 }}>
        <div style={{ fontSize: 28, color: '#C9A84C', marginBottom: 12 }}>✦</div>
        <h1 style={{
          fontFamily: "'Playfair Display', serif", fontSize: 26,
          fontWeight: 700, color: '#F5F0E8', marginBottom: 8,
        }}>
          {tr.birthDetailsTitle}
        </h1>
        <p style={{ fontSize: 14, color: '#8A8580' }}>{tr.birthDetailsSubtitle}</p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Date of Birth */}
        <div className="fade-in-up-d1">
          <label style={{
            display: 'block', fontSize: 12, color: '#C9A84C',
            marginBottom: 8, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            <Calendar size={12} style={{ display: 'inline', marginRight: 6 }} />
            {tr.dateOfBirth}
          </label>
          <input
            data-testid="input-birth-date"
            type="date"
            className="input-field"
            value={birthDate}
            onChange={e => setBirthDate(e.target.value)}
            required
            max={new Date().toISOString().split('T')[0]}
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
        </div>

        {/* Time of Birth — with clear button + unknown toggle */}
        <div className="fade-in-up-d2">
          <label style={{
            display: 'block', fontSize: 12, color: '#C9A84C',
            marginBottom: 8, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            <Clock size={12} style={{ display: 'inline', marginRight: 6 }} />
            {tr.timeOfBirth}
          </label>
          <div style={{ position: 'relative', opacity: unknownTime ? 0.4 : 1, transition: 'opacity 0.2s' }}>
            <input
              data-testid="input-birth-time"
              type="time"
              className="input-field"
              value={birthTime}
              onChange={e => setBirthTime(e.target.value)}
              disabled={unknownTime}
              style={{ width: '100%', boxSizing: 'border-box', paddingRight: birthTime ? 44 : 16 }}
            />
            {birthTime && !unknownTime && (
              <button
                type="button"
                data-testid="clear-time-btn"
                onClick={clearTime}
                style={{
                  position: 'absolute', right: 12, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none',
                  color: '#8A8580', cursor: 'pointer', padding: 2,
                  display: 'flex', alignItems: 'center',
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
          {/* Unknown time checkbox */}
          <label
            data-testid="unknown-time-label"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginTop: 10, cursor: 'pointer',
            }}
          >
            <input
              data-testid="unknown-time-checkbox"
              type="checkbox"
              checked={unknownTime}
              onChange={e => { setUnknownTime(e.target.checked); if (e.target.checked) setBirthTime(''); }}
              style={{ width: 16, height: 16, accentColor: '#C9A84C', cursor: 'pointer' }}
            />
            <span style={{ fontSize: 12, color: '#8A8580' }}>{tr.unknownTime}</span>
          </label>
        </div>

        {/* City search */}
        <div className="fade-in-up-d3" ref={dropdownRef} style={{ position: 'relative' }}>
          <label style={{
            display: 'block', fontSize: 12, color: '#C9A84C',
            marginBottom: 8, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            <MapPin size={12} style={{ display: 'inline', marginRight: 6 }} />
            {tr.placeOfBirth}
          </label>
          <div style={{ position: 'relative' }}>
            <input
              data-testid="input-birth-city"
              type="text"
              className="input-field"
              placeholder={tr.searchCity}
              value={cityQuery}
              onChange={e => {
                setCityQuery(e.target.value);
                setSelected(null);
              }}
              autoComplete="off"
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
            {searching && (
              <div style={{
                position: 'absolute', right: 14, top: '50%',
                transform: 'translateY(-50%)', color: '#8A8580', fontSize: 12,
              }}>
                ↻
              </div>
            )}
          </div>

          {/* Dropdown */}
          {showDropdown && cities.length > 0 && (
            <div
              data-testid="city-dropdown"
              style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                background: '#1a2335', border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: 12, marginTop: 4, overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              }}
            >
              {cities.map((city, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`city-option-${i}`}
                  onMouseDown={(e) => { e.preventDefault(); pickCity(city); }}
                  style={{
                    width: '100%', textAlign: 'left', padding: '12px 16px',
                    background: 'transparent', border: 'none',
                    color: '#F5F0E8', cursor: 'pointer', fontSize: 13,
                    borderBottom: i < cities.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                  }}
                >
                  <MapPin size={12} style={{ display: 'inline', marginRight: 6, color: '#C9A84C' }} />
                  {city.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected city confirmation */}
        {selected && (
          <div style={{
            padding: '10px 14px',
            background: 'rgba(45,106,79,0.15)',
            border: '1px solid rgba(45,106,79,0.3)', borderRadius: 10, fontSize: 13,
          }}>
            <span style={{ color: '#2D6A4F' }}>✓ </span>
            <span style={{ color: '#F5F0E8' }}>{selected.name}</span>
            <span style={{ color: '#8A8580', fontSize: 11, marginLeft: 8 }}>
              ({selected.lat.toFixed(2)}, {selected.lng.toFixed(2)})
            </span>
          </div>
        )}

        {error && (
          <p data-testid="birth-form-error" style={{ color: '#C0392B', fontSize: 13 }}>
            {error}
          </p>
        )}

        <button
          data-testid="generate-chart-btn"
          type="submit"
          className="btn-primary"
          disabled={loading || !birthDate || !selected}
          style={{ marginTop: 8, width: '100%' }}
        >
          {loading ? (tr.chartGenerating || 'Jyoti is reading your chart…') : tr.generateChart}
        </button>
      </form>
    </div>
  );
}
